import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import DirectionButton from "@components/buttons/DirectionButton";
import Document from "@components/drive/Document";
import DocumentPreview from "@components/drive/DocumentPreview";
import styles from "@components/drive/Drive.module.css";
import DriveSearch from "@components/drive/DriveSearch";
import FileResourceForm from "@components/forms/FileResourceForm";
import Modal from "@components/Modal";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import { FileResourceFragment, FolderFragment, GetJourneyWithDocumentsQuery } from "@typing/Generated";
import { getFolderTitle, sortByTimestamp } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Journey = GetJourneyWithDocumentsQuery["journey"];

type Props = {
  folder: FolderFragment;
  journey: Journey;
};

const Folder = ({ folder, journey }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const [editingFileResource, setEditingFileResource] = useState<FileResourceFragment | null>(null);
  const [previewingFileResource, setPreviewingFileResource] = useState<FileResourceFragment | null>(null);

  const fileResourceOpener = useOpener();
  const fileResourcePreviewOpener = useOpener();

  const handleFileResourceCreate = () => {
    setEditingFileResource(null);
    fileResourceOpener.open();
  };

  const handlePreviewFileResource = (fileResource: FileResourceFragment) => {
    setPreviewingFileResource(fileResource);
    fileResourcePreviewOpener.open();
  };

  const fileResources = sortByTimestamp(
    journey.fileResources.filter(file => file.folder?.id === folder.id),
    file => file.updatedAt
  );

  return (
    <div className={styles.folderSection}>
      <div>
        <DirectionButton
          direction="back"
          routerLink={journeyRoute({ journeyId: journey.id, linkAfterJourney: "/drive", myClient })}
        />
      </div>
      <div className={styles.folderHeader}>
        <div className={styles.noWrap}>
          <Heading2>{getFolderTitle(folder, intl)}</Heading2>
        </div>
        <div className={styles.folderSearchMaxWidth}>
          <DriveSearch folder={folder} journey={journey} />
        </div>
      </div>

      <div className={styles.folderFiles}>
        <div>
          <ButtonLink onClick={handleFileResourceCreate} stylized={false}>
            <div className={styles.uploadDocSmall}>
              <div>
                <Icon color="primary" icon="add" />
                <div className={styles.uploadDocText}>
                  <BodyCopy color="primary">
                    <FormattedMessage id="pages.drive.uploadDocument" />
                  </BodyCopy>
                </div>
              </div>
            </div>
          </ButtonLink>
        </div>
        {fileResources.map(fileResource => (
          <div className={styles.documentWidth} key={fileResource.id}>
            <Document
              fileResource={fileResource}
              handlePreviewFileResource={handlePreviewFileResource}
              journey={journey}
              key={fileResource.id}
            />
          </div>
        ))}
      </div>

      <DocumentPreview fileResource={previewingFileResource} journey={journey} opener={fileResourcePreviewOpener} />

      <Modal
        opener={fileResourceOpener}
        title={intl.formatMessage({
          id: editingFileResource?.worksheet
            ? "pages.documents.saveWorksheet"
            : editingFileResource
              ? "pages.documents.editFileResource"
              : "pages.documents.uploadFile"
        })}
      >
        {({ modalDisplayed }) => (
          <FileResourceForm
            autofocus={modalDisplayed}
            fileResource={editingFileResource}
            folder={folder}
            journey={journey}
            onCancel={fileResourceOpener.close}
            onSuccess={fileResourceOpener.close}
          />
        )}
      </Modal>
    </div>
  );
};

export default Folder;
